// Recovers from stale-chunk failures after a deploy — the Vue 2/webpack
// counterpart of shared-vue3's preloadErrorRecovery. Every route in these
// modules lazy-loads via `() => import(...)`; when a deploy invalidates the
// hashed chunk filenames a stale index references, navigation clicks die with
// "Loading chunk N failed" / "Loading CSS chunk N failed". vue-router funnels
// the rejection to router.onError, where a reload picks up a fresh index (and
// lets edge caches settle), which heals it.
//
// Guard rails, because a reload is disruptive and can mask real breakage:
//  - RELOAD_WINDOW_MS: a failure shortly after a reload means the reload
//    didn't help — report to Sentry instead of reloading again.
//  - MAX_RELOADS per ATTEMPT_RESET_MS: with slower navigation cadence a
//    genuinely broken asset would otherwise trigger a reload on every
//    attempt, forever. After the budget is spent, recovery stands down.
//  - BURST_WINDOW_MS: one navigation can fail several chunks at once;
//    swallow the follow-ups only briefly, since location.reload() can be
//    vetoed by a beforeunload dialog (dirty form, user clicks "Stay").
//
// The reload budget is persisted two ways so convergence never depends on a
// single channel: sessionStorage (primary) AND a URL query marker. The marker
// keeps a browser with sessionStorage unavailable (private modes, storage
// disabled, sandboxed contexts) from reloading forever on a genuinely broken
// chunk — the count rides across reloads in the URL. We read it on install and
// strip it immediately, so a healed load leaves a clean address bar. (Mirrors
// the Vue 3 preloadErrorRecovery hardening.)
//
// Sentry visibility: each module's ignoreErrors anchors the raw
// "Loading chunk … failed" message, so both recovered failures and the
// pre-reload burst stay quiet. When recovery stands down, the raw error is
// still ignored — so this util reports it explicitly under a
// ChunkLoadRecoveryExhausted wrapper, which the anchored pattern lets through.
//
// NOTE: this only recovers route-navigation chunk failures (router.onError).
// On-interaction import() sites in modules/shared (export/PDF: exceljs, jspdf)
// are outside this path — the same scope boundary as the Vue 3 side.
//
// TESTS: this shared dir has no local test runner; the suite lives at
// modules/corporate/src/utils/__tests__/chunkLoadErrorRecovery.test.js and
// imports this util directly. Keep it in sync when editing here.

export const CHUNK_RELOAD_KEY = 'portal:chunk-error-recovery'
export const CHUNK_RECOVERY_PARAM = '__chunkRecovery'
export const RELOAD_WINDOW_MS = 60_000
export const BURST_WINDOW_MS = 5_000
export const MAX_RELOADS = 2
export const ATTEMPT_RESET_MS = 30 * 60_000

export function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError') return true
  return /^Loading (CSS )?chunk \S+ failed/.test(error.message || '')
}

function readStorageState(target) {
  try {
    const raw = target.sessionStorage.getItem(CHUNK_RELOAD_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      const at = Number(parsed?.at)
      const count = Number(parsed?.count)
      // Clamp count to >= 0: a negative value (corrupt storage, or a crafted URL
      // marker — see readUrlState) would slip past the count >= MAX_RELOADS cap
      // and widen the reload budget.
      if (Number.isFinite(at) && Number.isFinite(count)) return { at, count: Math.max(0, count) }
    }
  } catch {
    // sessionStorage unavailable or malformed — the URL marker still carries
    // the count across reloads (see readUrlState).
  }
  return { at: 0, count: 0 }
}

function writeStorageState(target, state) {
  try {
    target.sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(state))
  } catch {
    // Ignore — the URL marker is the storage-independent fallback.
  }
}

function readUrlState(target) {
  try {
    const url = new URL(target.location.href)
    const raw = url.searchParams.get(CHUNK_RECOVERY_PARAM)
    if (raw) {
      const parts = raw.split('.')
      const count = Number(parts[0])
      const at = Number(parts[1])
      // Clamp count to >= 0: `?__chunkRecovery=-5.0` is finite but negative, so
      // it would otherwise pass the count >= MAX_RELOADS cap and widen the budget.
      if (Number.isFinite(at) && Number.isFinite(count)) return { at, count: Math.max(0, count) }
    }
  } catch {
    // malformed / no URL — treat as no history
  }
  return { at: 0, count: 0 }
}

function setUrlMarker(target, state) {
  try {
    const url = new URL(target.location.href)
    url.searchParams.set(CHUNK_RECOVERY_PARAM, `${state.count}.${state.at}`)
    target.history.replaceState(target.history.state ?? null, '', url.toString())
  } catch {
    // Ignore — worst case we fall back to sessionStorage-only convergence.
  }
}

function stripUrlMarker(target) {
  try {
    const url = new URL(target.location.href)
    if (!url.searchParams.has(CHUNK_RECOVERY_PARAM)) return
    url.searchParams.delete(CHUNK_RECOVERY_PARAM)
    target.history.replaceState(target.history.state ?? null, '', url.toString())
  } catch {
    // Ignore — a lingering marker is harmless (self-clears on next navigation).
  }
}

function reportPersistentFailure(sentry, error) {
  if (!sentry?.captureException) return
  const wrapped = new Error(`Chunk load failure persisted after reload recovery: ${error?.message}`)
  wrapped.name = 'ChunkLoadRecoveryExhausted'
  sentry.captureException(wrapped, { tags: { chunkLoadRecovery: 'exhausted' } })
}

export function installChunkLoadErrorRecovery({ router, sentry, target = window }) {
  // Seed the cross-reload budget from whichever channel survived this reload,
  // preferring the most recently written. The URL marker is then stripped so a
  // healed load leaves a clean address bar; the count lives on in memory (and
  // sessionStorage where available) for the life of this page instance.
  const storageState = readStorageState(target)
  const urlState = readUrlState(target)
  let state = urlState.at >= storageState.at ? urlState : storageState
  stripUrlMarker(target)

  let reloadRequestedAt = 0

  router.onError((error) => {
    if (!isChunkLoadError(error)) return

    // Offline: a reload can't fetch a fresh index with no network — it would drop
    // the user into the browser's offline page and lose the SPA (and anything in
    // memory). Leave the failed click as a no-op, matching pre-recovery behavior
    // (the raw error stays ignored). These modules run on warehouse/dispensary
    // wifi, so this path is live. Mirrors the navigator.onLine guard in
    // shared-vue3's preloadErrorRecovery (optional-chained here since the vue2
    // target is `window` and we never want a missing navigator to crash onError).
    if (target.navigator?.onLine === false) return

    const now = Date.now()

    if (reloadRequestedAt && now - reloadRequestedAt < BURST_WINDOW_MS) {
      // A burst of failures before the reload lands — already handled.
      return
    }

    // Reloaded moments ago and it didn't help — report instead of looping.
    if (now - state.at < RELOAD_WINDOW_MS) {
      reportPersistentFailure(sentry, error)
      return
    }

    // Attempt budget: reset after a quiet period, otherwise enforce the cap.
    const count = now - state.at < ATTEMPT_RESET_MS ? state.count : 0
    if (count >= MAX_RELOADS) {
      reportPersistentFailure(sentry, error)
      return
    }

    const next = { at: now, count: count + 1 }
    state = next
    writeStorageState(target, next)
    setUrlMarker(target, next)
    reloadRequestedAt = now
    target.location.reload()
  })
}
